import React, { useEffect, useMemo, useRef, useState } from 'react';

type LatLng = { lat: number; lng: number };

export type PlaceSelection = {
  name?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  coordinates?: LatLng;
};

interface LeafletPickerProps {
  value?: PlaceSelection;
  onChange: (next: PlaceSelection) => void;
  height?: number | string;
  defaultCenter?: LatLng;
  disabled?: boolean;
}

function ensureLeafletAssets() {
  if (typeof document === 'undefined') return;
  const cssId = 'leaflet-css';
  const jsId = 'leaflet-js';
  if (!document.getElementById(cssId)) {
    const link = document.createElement('link');
    link.id = cssId;
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);
  }
  if (!document.getElementById(jsId)) {
    const script = document.createElement('script');
    script.id = jsId;
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.async = true;
    document.body.appendChild(script);
  }
}

export default function LeafletPicker({ value, onChange, height = 'min(300px,45vh)', defaultCenter, disabled }: LeafletPickerProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const markerRef = useRef<any>(null);
  const mapRef = useRef<any>(null);
  const [ready, setReady] = useState<boolean>(false);
  const [center, setCenter] = useState<LatLng>(
    value?.coordinates || defaultCenter || { lat: -1.286389, lng: 36.817223 }
  );
  const [query, setQuery] = useState<string>('');

  useEffect(() => {
    ensureLeafletAssets();
    const check = () => {
      if ((window as any).L) setReady(true);
      else setTimeout(check, 150);
    };
    check();
  }, []);

  // Sync external value -> center/marker when coordinates change
  useEffect(() => {
    const coords = value?.coordinates;
    if (!coords) return;
    setCenter(coords);
    if (!ready) return;
    try {
      if (mapRef.current) {
        const currentZoom = mapRef.current.getZoom?.() ?? 12;
        mapRef.current.setView([coords.lat, coords.lng], Math.max(currentZoom, 15));
      }
      if (markerRef.current) {
        markerRef.current.setLatLng([coords.lat, coords.lng]);
      }
    } catch {}
  }, [ready, value?.coordinates?.lat, value?.coordinates?.lng]);

  useEffect(() => {
    if (!ready || !containerRef.current) return;
    const L = (window as any).L;
    if (!mapRef.current) {
      mapRef.current = L.map(containerRef.current).setView([center.lat, center.lng], value?.coordinates ? 15 : 12);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
      }).addTo(mapRef.current);
    } else {
      mapRef.current.setView([center.lat, center.lng]);
    }

    if (markerRef.current) {
      markerRef.current.setLatLng([center.lat, center.lng]);
    } else {
      markerRef.current = L.marker([center.lat, center.lng]).addTo(mapRef.current);
    }

    const onClick = (e: any) => {
      if (disabled) return;
      const coords = { lat: e.latlng.lat, lng: e.latlng.lng };
      setCenter(coords);
      markerRef.current.setLatLng([coords.lat, coords.lng]);
      // Reverse geocode via Nominatim
      fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${coords.lat}&lon=${coords.lng}`)
        .then((r) => r.json())
        .then((j) => {
          const address = j?.display_name || '';
          const comps = j?.address || {};
          onChange({
            ...value,
            address,
            city: comps.city || comps.town || comps.village || value?.city,
            state: comps.state || value?.state,
            country: comps.country || value?.country,
            coordinates: coords,
          });
        })
        .catch(() => onChange({ ...value, coordinates: coords }));
    };

    mapRef.current.on('click', onClick);
    return () => {
      if (mapRef.current) mapRef.current.off('click', onClick);
    };
  }, [ready, center.lat, center.lng, disabled]);

  const findQuery = async () => {
    if (!query.trim()) return;
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`);
      const arr = await res.json();
      if (arr && arr[0]) {
        const coords = { lat: parseFloat(arr[0].lat), lng: parseFloat(arr[0].lon) };
        setCenter(coords);
        if (markerRef.current) markerRef.current.setLatLng([coords.lat, coords.lng]);
        onChange({ ...value, address: arr[0].display_name, coordinates: coords });
      }
    } catch {}
  };

  const containerStyle = useMemo(() => ({ height, width: '100%', borderRadius: 12 } as React.CSSProperties), [height]);

  return (
    <div className="space-y-2">
      <div className="flex gap-2 flex-col sm:flex-row">
        <input
          className="input flex-1"
          placeholder="Search address (OSM)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={disabled}
        />
        <button type="button" className="btn bg-teal-600 text-white" disabled={disabled} onClick={findQuery}>Find</button>
      </div>
      <div className="rounded-xl overflow-hidden border" style={{ ...containerStyle, borderColor: 'var(--border)' }} ref={containerRef} />
      {!ready && <div className="text-sm text-theme-secondary">Loading map…</div>}
    </div>
  );
}